// Small building blocks local to the closeout feature. Candidates for the shared kit
// (src/components) - proposed to the integrator rather than edited there.
import { useQueryClient } from '@tanstack/react-query'
import { useId, type ReactNode } from 'react'
import { ErrorBanner, RuleRefs } from '@/components/evidence'
import { Badge, Banner, Button, cx, RadioGroup } from '@/components/ui'
import type { AvailableAction } from '@/domain/types'
import { PtwError } from '@/lib/commands'
import { humanize, yesNoText, type Hint } from './logic'

type YesNoOption = { label: ReactNode; hint?: ReactNode }

/** An explicit Yes/No answer: nothing is preselected, so the person must choose. */
export function YesNo({ name, value, onChange, yes, no }: {
  name: string
  value: boolean | null
  onChange: (v: boolean) => void
  yes: YesNoOption
  no: YesNoOption
}) {
  return (
    <RadioGroup<'yes' | 'no'>
      name={name}
      value={value === null ? null : value ? 'yes' : 'no'}
      onChange={(v) => onChange(v === 'yes')}
      options={[
        { value: 'yes', label: yes.label, hint: yes.hint },
        { value: 'no', label: no.label, hint: no.hint },
      ]}
    />
  )
}

/**
 * One deliberate tick. The whole row is the tap target and at least 44px tall (the shared
 * Checkbox row is about 36px; proposed to the integrator).
 */
export function Tick({ checked, onChange, label, hint, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  return (
    <label htmlFor={id} className={cx('flex min-h-[44px] cursor-pointer items-start gap-3 py-3', disabled && 'cursor-not-allowed opacity-60')}>
      <input id={id} type="checkbox" className="mt-0.5 shrink-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-ink">
        {label}
        {hint ? <span className="mt-0.5 block text-xs text-ink-soft">{hint}</span> : null}
      </span>
    </label>
  )
}

/** A list of confirmations; nothing is pre-ticked and each must be ticked deliberately. */
export function TickList({ options, value, onChange, disabled }: {
  options: readonly { code: string; label: string; help?: string }[]
  value: Record<string, boolean>
  onChange: (next: Record<string, boolean>) => void
  disabled?: boolean
}) {
  return (
    <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
      {options.map((o) => (
        <Tick
          key={o.code}
          checked={value[o.code] === true}
          disabled={disabled}
          onChange={(v) => onChange({ ...value, [o.code]: v })}
          label={o.label}
          hint={o.help}
        />
      ))}
    </div>
  )
}

/**
 * Yes/No as a word, never colour alone. `judge` colours a declaration (Yes = done,
 * No = missing); facts such as "tools removed" stay neutral.
 */
export function YesNoBadge({ value, judge = false }: { value: boolean | null | undefined; judge?: boolean }) {
  const tone = !judge || value === null || value === undefined ? 'neutral' : value ? 'ok' : 'bad'
  return <Badge tone={tone}>{yesNoText(value)}</Badge>
}

/** A signed list of yes/no items; codes the catalogue does not know are still shown. */
export function CheckRecord({ options, values, judge = true }: {
  options: readonly { code: string; label: string }[]
  values: Record<string, boolean> | null | undefined
  judge?: boolean
}) {
  const v = values ?? {}
  const known = new Set(options.map((o) => o.code))
  const rows = [
    ...options.map((o) => ({ code: o.code, label: o.label, value: v[o.code] })),
    ...Object.keys(v)
      .filter((k) => !known.has(k))
      .map((k) => ({ code: k, label: humanize(k), value: v[k] })),
  ]
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-white">
      {rows.map((r) => (
        <li key={r.code} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
          <span className="text-ink">{r.label}</span>
          <YesNoBadge value={r.value} judge={judge} />
        </li>
      ))}
    </ul>
  )
}

/** A labelled group of radios or ticks (fieldset + legend, styled like Field). */
export function Question({ legend, hint, required, children }: { legend: ReactNode; hint?: ReactNode; required?: boolean; children: ReactNode }) {
  return (
    <fieldset className="mb-4 min-w-0">
      <legend className="mb-1 block text-sm font-semibold text-ink">
        {legend}
        {required ? (
          <span className="ml-1 text-bad" aria-hidden>
            *
          </span>
        ) : null}
      </legend>
      {children}
      {hint ? <p className="mt-1 text-xs text-ink-soft">{hint}</p> : null}
    </fieldset>
  )
}

/** Early, non-blocking hints that mirror server rules; the server still decides. */
export function HintList({ hints }: { hints: Hint[] }) {
  if (hints.length === 0) return null
  return (
    <Banner tone="warn" title="Check your answers">
      <ul className="list-disc space-y-1 pl-5">
        {hints.map((h) => (
          <li key={h.code}>
            {h.message}
            <RuleRefs ruleIds={h.rule_ids} />
          </li>
        ))}
      </ul>
    </Banner>
  )
}

/** Choices still to make before the step can be sent. */
export function Unanswered({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="rounded-lg border border-dashed border-line-strong bg-white p-3 text-sm text-ink">
      <p className="font-semibold">Before you send:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-soft">
        {items.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </div>
  )
}

/** States exactly what the person is declaring with their signature. */
export function SigningStatement({ title = 'What you are signing', children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-lg border-2 border-brand/40 bg-brand-light p-3 text-sm text-ink">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-dark">{title}</p>
      <div className="mt-1 space-y-1">{children}</div>
      <p className="mt-2 text-xs text-ink-soft">
        Your name, role and the time are taken from your own signed-in session and the server clock, not from this device.
      </p>
    </div>
  )
}

/** The server's own reason why this step is not open to the viewer now. */
export function NotAvailable({ action }: { action: AvailableAction }) {
  if (action.enabled) return null
  return (
    <Banner tone="warn" title="You cannot do this step now">
      {action.reason ?? 'The server did not give a reason.'}
    </Banner>
  )
}

/** Command failure with every blocker; offers a reload when someone else changed the permit. */
export function CommandFailure({ error }: { error: unknown }) {
  const qc = useQueryClient()
  if (!error) return null
  const e = PtwError.from(error)
  return (
    <div className="space-y-2">
      <ErrorBanner error={e} />
      {e.code === 'PW002' ? (
        <Button variant="secondary" onClick={() => void qc.invalidateQueries()}>
          Load the latest version
        </Button>
      ) : null}
    </div>
  )
}

export function PanelFooter({ children }: { children: ReactNode }) {
  return <div className="mt-2 flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:justify-end">{children}</div>
}

/** Octagon stop sign: shape and word, not colour alone. */
export function StopSign({ className = 'h-14 w-14' }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={`shrink-0 text-bad ${className}`} aria-hidden>
      <polygon points="14,1 34,1 47,14 47,34 34,47 14,47 1,34 1,14" fill="#ffffff" />
      <polygon points="15,4 33,4 44,15 44,33 33,44 15,44 4,33 4,15" fill="currentColor" />
      <text x="24" y="28.5" textAnchor="middle" fontSize="12" fontWeight="700" fill="#ffffff" fontFamily="Arial, sans-serif">
        STOP
      </text>
    </svg>
  )
}
